// Upstream: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/table.tsx — MIT. Cell/row classes from @ahcut/ui/recipes.

import type { ComponentProps, ReactNode } from "react";
import {
  tableCellClass,
  tableClass,
  tableHeadCellClass,
  tableRowHoverClass,
} from "@ahcut/ui/recipes";
import { cn } from "@/lib/cn";

export interface TableWrapperProps extends ComponentProps<"div"> {
  /** Accessible name. With it the wrapper is a named region in the tab order, so a
   *  keyboard user can scroll a table that outgrows it (ui/DESIGN.md §5). */
  label?: string;
}

/** Horizontal scroll container — wide tables scroll inside it, never the page. */
export function TableWrapper({ label, className, ...rest }: TableWrapperProps) {
  return (
    <div
      data-slot="table-wrapper"
      role={label ? "region" : undefined}
      aria-label={label}
      tabIndex={label ? 0 : undefined}
      className={cn("relative w-full overflow-x-auto", label && "focus-visible:-outline-offset-2", className)}
      {...rest}
    />
  );
}

export function Table({ className, ...rest }: ComponentProps<"table">) {
  return <table data-slot="table" className={cn(tableClass, className)} {...rest} />;
}

export function TableHeader({ className, ...rest }: ComponentProps<"thead">) {
  return <thead data-slot="table-header" className={className} {...rest} />;
}

export function TableBody({ className, ...rest }: ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&>tr:last-child>td]:border-b-0", className)}
      {...rest}
    />
  );
}

export function TableRow({ className, ...rest }: ComponentProps<"tr">) {
  return <tr data-slot="table-row" className={cn(tableRowHoverClass, className)} {...rest} />;
}

export function TableHead({ className, scope = "col", ...rest }: ComponentProps<"th">) {
  return <th data-slot="table-head" scope={scope} className={cn(tableHeadCellClass, className)} {...rest} />;
}

export function TableCell({ className, ...rest }: ComponentProps<"td">) {
  return <td data-slot="table-cell" className={cn(tableCellClass, className)} {...rest} />;
}

export function TableCaption({ className, ...rest }: ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("caption-bottom pt-3 text-left text-sm text-fg-2", className)}
      {...rest}
    />
  );
}

/** Single full-width row for "nothing here" inside an otherwise normal table. */
export function TableEmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr data-slot="table-empty-row">
      <td colSpan={colSpan} className={cn(tableCellClass, "text-fg-2")}>
        {children}
      </td>
    </tr>
  );
}
